import { useEffect, useState, useCallback } from "react";
import { Save, Lock, Unlock, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const inputCls = "bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none";

export default function AdminDrip() {
  const [drip, setDrip] = useState(null);
  const [grantForm, setGrantForm] = useState({ email: "", module_id: "mod-01" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/drip");
      setDrip(data);
    } catch {
      toast.error("Failed to load drip settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/drip", { ...drip, ...patch });
      setDrip(data);
      toast.success("Drip settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setGrant = async (action) => {
    try {
      const { data } = await api.post("/admin/drip/grants", { ...grantForm, action });
      setDrip(data);
      toast.success(action === "clear" ? "Override cleared" : `Module ${action === "grant" ? "unlocked" : "locked"} for member`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!drip) return <p className="text-slate-500 text-sm">Loading drip settings…</p>;

  return (
    <div data-testid="admin-drip" className="max-w-3xl space-y-6">
      <div className="bg-charcoal border border-white/10 p-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-2">[MODULE DRIP SCHEDULE]</p>
            <p className="text-slate-300 text-sm leading-relaxed">
              Off by default — Founding Cohort members get every published module and template immediately.
              Turn it on only when you want modules to unlock over time.
            </p>
          </div>
          <button
            onClick={() => save({ drip_enabled: !drip.drip_enabled })}
            data-testid="drip-enable-toggle"
            className={`shrink-0 font-mono text-xs uppercase tracking-wider px-4 py-2.5 border transition-colors ${
              drip.drip_enabled ? "bg-safety/15 text-safety border-safety/50" : "border-white/15 text-slate-400 hover:border-white/40"
            }`}
          >
            {drip.drip_enabled ? "Enabled" : "Disabled"}
          </button>
        </div>

        {drip.drip_enabled && (
          <div className="mt-6 pt-6 border-t border-white/10 space-y-5" data-testid="drip-config">
            <div>
              <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2.5">Unlock mode</label>
              <div className="grid sm:grid-cols-2 gap-2.5">
                <button onClick={() => save({ drip_mode: "weekly" })} data-testid="drip-mode-weekly"
                  className={`text-left p-4 border transition-colors ${drip.drip_mode === "weekly" ? "border-safety bg-safety/10" : "border-white/15 hover:border-white/40"}`}>
                  <p className="text-white font-medium text-sm">Weeks after enrollment</p>
                  <p className="text-slate-500 text-xs mt-1">Module 1 unlocks at enrollment, Module 2 a week later, and so on — per member.</p>
                </button>
                <button onClick={() => save({ drip_mode: "cohort" })} data-testid="drip-mode-cohort"
                  className={`text-left p-4 border transition-colors ${drip.drip_mode === "cohort" ? "border-safety bg-safety/10" : "border-white/15 hover:border-white/40"}`}>
                  <p className="text-white font-medium text-sm">Cohort start date</p>
                  <p className="text-slate-500 text-xs mt-1">Everyone unlocks together — Module 1 on the cohort date, one per week after.</p>
                </button>
              </div>
            </div>
            {drip.drip_mode === "cohort" && (
              <div>
                <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2">Cohort start date</label>
                <div className="flex gap-3">
                  <input type="date" data-testid="drip-cohort-date" value={(drip.drip_cohort_date || "").slice(0, 10)}
                    onChange={(e) => setDrip({ ...drip, drip_cohort_date: e.target.value })}
                    className={inputCls} />
                  <button onClick={() => save({ drip_cohort_date: drip.drip_cohort_date })} data-testid="drip-save-date"
                    disabled={saving}
                    className="bg-safety hover:bg-safety-hover disabled:opacity-50 text-white font-semibold px-5 py-2.5 text-sm flex items-center gap-2 transition-colors">
                    <Save size={14} /> Save date
                  </button>
                </div>
              </div>
            )}
            <p className="text-slate-500 text-xs leading-relaxed border-l-2 border-safety/50 pl-3">
              Safety rules: a module a member already completed is never locked, and a manual unlock always wins.
            </p>
          </div>
        )}
      </div>

      <div className="bg-charcoal border border-white/10 p-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-2">[MANUAL MEMBER OVERRIDES]</p>
        <p className="text-slate-400 text-sm mb-5">Unlock or lock a specific module for a specific member, regardless of schedule.</p>
        <div className="flex flex-wrap gap-3">
          <input type="email" placeholder="member@email.com" data-testid="grant-email-input" value={grantForm.email}
            onChange={(e) => setGrantForm({ ...grantForm, email: e.target.value })} className={`${inputCls} flex-1 min-w-[220px]`} />
          <select value={grantForm.module_id} data-testid="grant-module-select"
            onChange={(e) => setGrantForm({ ...grantForm, module_id: e.target.value })} className={inputCls}>
            {drip.modules.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.title}</option>)}
          </select>
          <button onClick={() => setGrant("grant")} disabled={!grantForm.email} data-testid="grant-unlock-btn"
            className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25 disabled:opacity-40 font-semibold px-4 py-2.5 text-sm flex items-center gap-2 transition-colors">
            <Unlock size={14} /> Unlock
          </button>
          <button onClick={() => setGrant("lock")} disabled={!grantForm.email} data-testid="grant-lock-btn"
            className="bg-red-500/10 text-red-400 border border-red-500/40 hover:bg-red-500/20 disabled:opacity-40 font-semibold px-4 py-2.5 text-sm flex items-center gap-2 transition-colors">
            <Lock size={14} /> Lock
          </button>
        </div>
        <div className="mt-5 space-y-px border border-white/10">
          {drip.grants.length === 0 && <p className="bg-navy p-4 text-slate-500 text-sm">No manual overrides set.</p>}
          {drip.grants.map((g) => (
            <div key={`${g.email}-${g.module_id}`} className="bg-navy px-4 py-3 flex items-center gap-3" data-testid={`grant-row-${g.email.split("@")[0]}-${g.module_id}`}>
              {g.action === "grant" ? <Unlock size={14} className="text-emerald-400" /> : <Lock size={14} className="text-red-400" />}
              <p className="text-sm text-slate-300 flex-1">{g.email} — {drip.modules.find((m) => m.id === g.module_id)?.code} {g.action === "grant" ? "unlocked" : "locked"}</p>
              <button onClick={async () => { setGrantForm({ email: g.email, module_id: g.module_id }); await api.post("/admin/drip/grants", { email: g.email, module_id: g.module_id, action: "clear" }); load(); }}
                data-testid={`grant-clear-${g.email.split("@")[0]}-${g.module_id}`}
                className="text-slate-600 hover:text-white transition-colors"><XCircle size={15} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
